/**
 * 권한 평가 단일 진입점.
 * SSOT: doc/system-completion-plan/03-mapping/detail/02-business-domains/entity.md
 *       (customPermissions: "subdomain:action" 형식)
 *       및 액션 매트릭스 docs/subdomain-action-matrix.html.
 */

import type { DomainPermissionLevel, TableUserPermissionContext } from '../types';

export interface PermissionCheckInput {
  userPermissionContext: Pick<TableUserPermissionContext, 'userId' | 'domainPermissions'>;
  businessDomain: string;
  action: 'read' | 'create' | 'update' | 'delete' | string;
  /** When true, mutating actions (create/update/delete) are denied */
  readOnly?: boolean;
}

export interface PermissionCheckResult {
  allowed: boolean;
  missingKey: string;
  reason?: string;
}

function hasDomainPermission(
  domainPermissions: DomainPermissionLevel[],
  businessDomain: string,
  key: string
): boolean {
  return domainPermissions.some((dp) => {
    if (dp.domain !== businessDomain) {
      return false;
    }
    return dp.permissions.some((permission) => String(permission) === key);
  });
}

export function checkPermission(input: PermissionCheckInput): PermissionCheckResult {
  const missingKey = `${input.businessDomain}:${input.action}`;

  if (input.readOnly && input.action !== 'read') {
    return {
      allowed: false,
      missingKey,
      reason: 'read-only mode',
    };
  }

  const allowed = hasDomainPermission(
    input.userPermissionContext.domainPermissions,
    input.businessDomain,
    missingKey
  );

  return {
    allowed,
    missingKey,
    ...(allowed ? {} : { reason: `권한 ${missingKey}가 필요합니다.` }),
  };
}
